import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import TigerDraw from "../components/TigerDraw";
import { CONTACT, SERVICES } from "../data/content";
import { FadeUp, SectionTag } from "../components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: params.get("service") || "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(
        res.data.auto_reply_sent
          ? "Brief received. A confirmation is on its way to your inbox — we'll bite back within 24 hours."
          : "Received. We'll bite back within 24 hours."
      );
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("That didn't go through. Try again or mail us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/20 bg-nexa-surface px-4 py-3.5 text-sm text-nexa-bone placeholder:text-nexa-bone/30 focus:border-nexa-red focus:outline-none focus:ring-0 transition-colors duration-200";

  return (
    <>
      <PageHeader
        tag="Contact — Open the cage"
        title={
          <>
            Bring us a brand<br />
            <span className="text-nexa-red">worth fighting for.</span>
          </>
        }
        sub="Brief us in five lines or five paragraphs. Either way, you get a point of view back — not a rate card."
      />

      <section className="mx-auto grid max-w-[1600px] gap-16 px-5 py-24 md:grid-cols-12 md:px-10" data-testid="contact-section">
        <div className="md:col-span-4">
          <FadeUp>
            <SectionTag>The den</SectionTag>
            <p className="mt-4 text-base leading-relaxed text-nexa-bone/70">{CONTACT.address}</p>
            <div className="mt-8 space-y-3 font-mono text-sm">
              <a href={`mailto:${CONTACT.email}`} className="link-line block w-fit text-nexa-bone/80" data-testid="contact-email-link">
                {CONTACT.email}
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="link-line block w-fit text-nexa-bone/80" data-testid="contact-phone-link">
                {CONTACT.phone}
              </a>
              <a href={`tel:${CONTACT.phone2Raw}`} className="link-line block w-fit text-nexa-bone/80" data-testid="contact-phone2-link">
                {CONTACT.phone2}
              </a>
              <a
                href={`https://wa.me/${CONTACT.phoneRaw.replace("+", "")}`}
                target="_blank"
                rel="noreferrer"
                className="link-line block w-fit text-nexa-red"
                data-testid="contact-whatsapp-link"
              >
                WhatsApp the den →
              </a>
            </div>
            <div className="mt-10 border border-white/10 p-6">
              <p className="font-display text-xl uppercase text-nexa-bone">Office hours</p>
              <p className="mt-2 text-sm text-nexa-bone/50">Mon–Sat, 10:00–19:00 IST. The internet, of course, never sleeps — and neither does your brand once we're on it.</p>
            </div>
            <div className="mt-8 -rotate-2 border border-white/15 bg-nexa-red p-3 shadow-[10px_10px_0_0_rgba(0,0,0,0.45)]" data-testid="contact-tiger">
              <TigerDraw n={2} className="h-auto w-full" startDelay={0.4} />
              <p className="px-2 pb-1 pt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/85">
                Every brief enters guarded territory.
              </p>
            </div>
          </FadeUp>
        </div>

        <div className="md:col-span-8">
          <FadeUp delay={0.1}>
            <form onSubmit={submit} className="grid gap-5" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-bone/50">Name *</label>
                  <input id="cf-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="contact-input-name" />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-bone/50">Email *</label>
                  <input id="cf-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@brand.com" data-testid="contact-input-email" />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-bone/50">Phone</label>
                  <input id="cf-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" data-testid="contact-input-phone" />
                </div>
                <div>
                  <label htmlFor="cf-service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-bone/50">What do you need?</label>
                  <select id="cf-service" value={form.service} onChange={set("service")} className={inputCls} data-testid="contact-select-service">
                    <option value="">Pick a weapon</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.id}>{s.title}</option>
                    ))}
                    <option value="everything">Everything — full brand attack</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="cf-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-bone/50">The brief *</label>
                <textarea id="cf-message" required rows={6} value={form.message} onChange={set("message")} className={inputCls} placeholder="What are we building, launching or fixing?" data-testid="contact-input-message" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group flex w-fit items-center gap-3 bg-nexa-red px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-nexa-bone hover:text-nexa-black disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send the brief"}
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Contact;
